import validator from 'validator';
import { DATE_FIELD_TYPE, DATETIME_FIELD_TYPE, DATETIME_LOCAL_FIELD_TYPE } from '../consts/consts';

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/** Matches YYYY-MM-DD date strings. */
export const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Israel date format for react-datepicker (dd/MM/yyyy). */
export const DATE_FORMAT_ISRAEL = 'dd/MM/yyyy';

/** Israel placeholder (DD/MM/YYYY). */
export const DATE_PLACEHOLDER_ISRAEL = 'DD/MM/YYYY';

function isValidYearRange(year: number): boolean {
  return year >= MIN_YEAR && year <= MAX_YEAR;
}

function toYYYYMMDD(year: number, month: number, day: number): string | null {
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  if (!isValidYearRange(year)) {
    return null;
  }
  const y = String(year);
  const m = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return `${y}-${m}-${dayStr}`;
}

/** Returns true when the given field_type is a date field (including legacy "datetime" saved by old console). */
export function isDateFieldType(fieldType: string | undefined): boolean {
  return (
    fieldType === DATE_FIELD_TYPE ||
    fieldType === DATETIME_LOCAL_FIELD_TYPE ||
    fieldType === DATETIME_FIELD_TYPE
  );
}

/**
 * Returns true for day-first locales (DD/MM/YYYY, e.g. he-IL, en-GB), false for month-first (MM/DD/YYYY, e.g. en-US).
 * Used so validation uses the normalized value from the user's locale.
 */
export function getPreferDayFirstFromLocale(): boolean {
  if (typeof navigator === 'undefined') return true;
  return !/^en-US(\b|$)/i.test(navigator.language);
}

/** Israel format: always DD/MM/YYYY. Use for display when app is Israel-only. */
export function formatDateToIsraelDisplay(yyyyMmDd: string): string {
  const trimmed = String(yyyyMmDd).trim();
  if (!YYYY_MM_DD_REGEX.test(trimmed)) return yyyyMmDd;
  const [y, m, d] = trimmed.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Normalizes a date string to YYYY-MM-DD. Respects user locale for ambiguous dates:
 * day-first (DD/MM/YYYY) for e.g. he-IL, en-GB; month-first (MM/DD/YYYY) for e.g. en-US.
 */
export function normalizeToYYYYMMDD(
  value: string,
  options?: { preferDayFirst?: boolean },
): string | null {
  const preferDayFirst = options?.preferDayFirst ?? true;
  const trimmed = String(value).trim();
  if (trimmed === '') {
    return null;
  }

  // Already YYYY-MM-DD
  if (YYYY_MM_DD_REGEX.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    const result = toYYYYMMDD(y, m, d);
    if (result) return result;
  }

  // ISO datetime: take date part
  if (trimmed.includes('T')) {
    const datePart = trimmed.split('T')[0].trim();
    if (YYYY_MM_DD_REGEX.test(datePart)) {
      const [y, m, d] = datePart.split('-').map(Number);
      if (toYYYYMMDD(y, m, d)) return datePart;
    }
  }

  // Parse DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY (day-first)
  const parts = trimmed
    .split(/[-/.]/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 3) {
    const numParts = parts.map((p) => parseInt(p, 10));
    if (numParts.some((n) => isNaN(n))) return null;
    let day: number, month: number, year: number;
    if (preferDayFirst) {
      // DD, MM, YYYY (Israeli format)
      day = numParts[0];
      month = numParts[1];
      year = numParts[2];
    } else {
      // MM, DD, YYYY (US-style)
      month = numParts[0];
      day = numParts[1];
      year = numParts[2];
    }
    if (year < 100) {
      year += year < 50 ? 2000 : 1900;
    }
    return toYYYYMMDD(year, month, day);
  }

  return null;
}

/**
 * Returns true if the value is a valid date (empty, or parseable to YYYY-MM-DD with year 1900–2100).
 * Uses locale-aware normalization (e.g. DD/MM/YYYY for he-IL). Single place for date validation.
 */
export function isValidDateValue(value: string): boolean {
  const trimmed = String(value).trim();
  if (trimmed === '') return true;
  const normalized = normalizeToYYYYMMDD(trimmed, {
    preferDayFirst: getPreferDayFirstFromLocale(),
  });
  const resolved = normalized ?? trimmed;
  const isValidFormat = validator.isDate(resolved, {
    format: 'YYYY-MM-DD',
    strictMode: true,
  });
  const parsed = new Date(resolved);
  const isRealistic =
    !isNaN(parsed.getTime()) &&
    parsed.getFullYear() >= MIN_YEAR &&
    parsed.getFullYear() <= MAX_YEAR;
  return !!(isValidFormat && isRealistic);
}
